// CaraLoom Knowledge Center — B2B guides for discharge planners, case
// managers, hospitals, health systems, and post-acute providers.
//
// Companion archive to data/articles.js but oriented at the post-acute
// care & discharge-planning platform positioning (iter 174). Rendered
// via GuidesPage (/guides) and GuidePage (/guides/:slug) — do not merge
// with the family-side articles archive; the audience and CTAs differ.
//
// Every guide here is professionally written, evidence-based where the
// underlying claim is verifiable (CMS regulations, HH-CoPs, LTACH
// criteria), and connected to CaraLoom pillar pages via internal links.

const REVIEWER = {
  name: 'The CaraLoom Clinical Team',
  role: 'Reviewed by RNs, case-management leaders, and platform engineering',
};

// Neutral stock cover for guide cards. Kept as text-only hero on the
// article surface so we don't accidentally source images with clinical
// content we can't verify.
const NEUTRAL_HERO = 'https://images.unsplash.com/photo-1585435557343-3b092031a831?crop=entropy&cs=srgb&fm=jpg&w=1200&h=675&fit=crop';

export const GUIDES = [
  // ─────────────────────────────────────────────────────────────
  // 1. Discharge Planning Checklist
  // ─────────────────────────────────────────────────────────────
  {
    slug: 'discharge-planning-checklist',
    category: 'Discharge Planning',
    title: 'The Hospital Discharge Planning Checklist',
    metaTitle: 'Hospital Discharge Planning Checklist — Every Step, in Order | CaraLoom',
    metaDescription:
      'The full hospital discharge planning checklist for case managers and social workers. Every step from disposition assessment through post-acute placement, patient choice documentation, and 30-day follow-through.',
    keywords: [
      'hospital discharge planning checklist',
      'discharge planning steps',
      'CMS discharge planning',
      'case manager checklist',
      'hospital to home checklist',
    ],
    excerpt:
      'A practical, sequenced checklist for hospital discharge planning — assessment, level of care, patient choice, coverage, referrals, warm hand-off, and 30-day follow-through.',
    heroImage: NEUTRAL_HERO,
    heroAlt: 'CaraLoom discharge planning checklist for hospital case managers',
    readMinutes: 9,
    datePublished: '2026-08-16',
    dateModified: '2026-08-16',
    reviewer: REVIEWER,
    tags: ['Discharge planning', 'Case management', 'CMS'],
    related: ['snf-vs-irf', 'levels-of-post-acute-care', 'home-health-eligibility'],
    faqs: [
      { q: 'When should discharge planning start?',
        a: 'CMS requires discharge planning to begin within 24 hours of admission, or earlier if the anticipated stay is short. In practice, an early start (day 0 or day 1) produces better placement outcomes because the case manager has time to work coverage, capacity, and patient choice in parallel.' },
      { q: 'What is the patient-choice requirement?',
        a: 'Under the CMS Discharge Planning Conditions of Participation (42 CFR 482.43), hospitals must provide patients with a list of qualifying Medicare-participating post-acute providers relevant to their needs and geographic area, and must document the patient\u2019s choice in the medical record. CaraLoom captures this natively with a time-stamped audit entry.' },
      { q: 'What is a "warm hand-off" and why does it matter?',
        a: 'A warm hand-off is a live, structured communication between the discharging clinical team and the receiving post-acute team (nurse-to-nurse for home health; nurse-to-nurse or physician-to-physician for SNF/IRF/LTACH). It reduces medication errors, missed follow-up appointments, and 30-day readmissions substantially versus a paper-only hand-off.' },
      { q: 'How long is the 30-day follow-through window?',
        a: '30 days from discharge is the standard readmission-tracking window used by CMS (Hospital Readmissions Reduction Program). Best-practice discharge planning treats the transition as an active workflow through day 30, not a closed event on the day of discharge.' },
    ],
    body: `A hospital discharge is a workflow, not a moment. Done well, it starts on admission, drives every day of the stay, and continues to the 30-day mark after the patient leaves. Done poorly, it is a fax queue, a phone tree, and a readmission.

This checklist walks through every step of a defensible, CMS-aligned discharge plan.

## 1. Anchor the plan on admission

Within 24 hours of admission — earlier if the anticipated stay is short — every patient should have a discharge planning assessment on file. Capture:

- **Home situation** — who lives with them, stairs, prior use of durable medical equipment, transportation.
- **Functional baseline** — ADLs and IADLs before admission, walker/cane use, cognition.
- **Clinical needs on admission** — the level-of-care question is usually answerable on day one for the majority of patients.
- **Payer status** — Medicare, Medicare Advantage, Medicaid, commercial, self-pay. Coverage rules differ per level of care.
- **Language, cultural, and communication needs** for both patient and family.

If any assessment element is missing 48 hours in, someone should be paged. In CaraLoom this is an exception-queue card.

## 2. Determine the level of post-acute care

Match the patient to the right site of care. The main levels are:

- **Skilled Nursing Facility (SNF)** — sub-acute rehab, ongoing skilled needs, wound care.
- **Home Health Agency (HHA)** — homebound, skilled intermittent nursing or therapy needs.
- **Inpatient Rehabilitation Facility (IRF)** — intensive multidisciplinary rehab, 3-hour therapy rule.
- **Long-Term Acute Care Hospital (LTACH)** — medically complex, extended stay, vent weaning.
- **Hospice** — terminal prognosis (\u22646 months) with comfort-focused goals of care.
- **Assisted Living / Memory Care** — custodial or supervised living, not a Medicare-covered post-acute benefit.

See the companion guides on [SNF vs IRF](/guides/snf-vs-irf), [levels of post-acute care](/guides/levels-of-post-acute-care), [home health eligibility](/guides/home-health-eligibility), and [hospice eligibility](/guides/hospice-eligibility) for the criteria.

## 3. Capture patient choice

CMS requires that patients be presented with a list of qualifying Medicare-participating post-acute providers relevant to their needs and geography, and that the patient\u2019s choice be documented in the medical record. The list must:

- be filtered by clinical fit (do they take this level of care?)
- include quality data available from CMS Care Compare when applicable
- include the disclosure that the hospital may have a financial relationship with any listed provider

CaraLoom generates the qualifying list automatically and stamps the patient\u2019s choice with a timestamp + user in an audit-safe entry.

## 4. Verify coverage before any signature

The worst outcome for a discharge is a placement the payer won\u2019t authorize. Run eligibility, benefits, and authorization before you route orders for signature. If coverage is a hold, the loop should pause and page a human — not fail silently.

## 5. Route the referral and manage the wave

Send the referral packet to the shortlisted qualifying providers. Best practices:

- Use offer waves (not blast-to-everyone) — first accept within a bounded window wins.
- Escalate a case that has zero accepts inside the SLA (typically 4-24 hours by level of care).
- Complex cases are the highest risk of decline; escalate them first.

## 6. Sign the standard order set

Once coverage clears, the standard order set drafts and signs digitally:

- **F2F** (Face-to-Face encounter documentation) — required for home health and hospice.
- **485 / Plan of Care** — home health.
- **NOA** (Notice of Admission) — home health.
- **Consent** — patient consent to services and to release information.

CaraLoom drafts these when coverage clears and routes them for digital signature in one thread.

## 7. Warm hand-off

Before or at the moment of discharge, complete a warm hand-off with the receiving post-acute team. This is a structured live conversation, not a fax, covering:

- Diagnoses, active problems, and recent changes.
- Medication reconciliation with any high-alert medications flagged.
- Pending test results and follow-up appointments.
- Advance care planning status (code status, MOLST/POLST, advance directive).
- Family caregiver contact and communication preferences.

## 8. Follow through for 30 days

The transition isn\u2019t over on discharge day. Track:

- **First visit completed** — for home health, the SOC visit must occur within 48 hours of the referral in most cases.
- **Medication adherence** — a nurse or pharmacist call within 72 hours of discharge is the single highest-yield readmission-prevention intervention.
- **Follow-up appointment attended** — track whether the follow-up PCP or specialist appointment happens.
- **Readmission signal** — any ED visit or admission within 30 days.

CaraLoom tracks all four automatically for placements coordinated through the platform.

## 9. Close the loop with data

Every discharge is a data point. Feed placement times, decline reasons, and readmission signals back into your case-management program monthly. The two questions leadership should be able to answer:

1. What is our median time-to-placement by level of care?
2. Which post-acute partners are delivering, and which aren\u2019t?

If you can\u2019t answer both, that\u2019s the first thing to fix.`,
  },

  // ─────────────────────────────────────────────────────────────
  // 2. SNF vs IRF
  // ─────────────────────────────────────────────────────────────
  {
    slug: 'snf-vs-irf',
    category: 'Levels of Care',
    title: 'SNF vs IRF — How to Choose the Right Rehab Setting',
    metaTitle: 'SNF vs IRF — Skilled Nursing vs Inpatient Rehab | CaraLoom',
    metaDescription:
      'SNF vs IRF explained for hospital case managers. Admission criteria, therapy intensity, length of stay, payer coverage, and which patients belong in each rehab setting.',
    keywords: [
      'SNF vs IRF',
      'skilled nursing vs inpatient rehab',
      'IRF admission criteria',
      '3 hour therapy rule',
      'post acute rehab decision',
    ],
    excerpt:
      'SNFs and IRFs both provide post-acute rehab — but they serve very different patients. Here\u2019s how to make the right call for each case.',
    heroImage: NEUTRAL_HERO,
    heroAlt: 'Comparing skilled nursing facility and inpatient rehabilitation facility criteria',
    readMinutes: 6,
    datePublished: '2026-08-16',
    dateModified: '2026-08-16',
    reviewer: REVIEWER,
    tags: ['SNF', 'IRF', 'Rehabilitation', 'Level of care'],
    related: ['levels-of-post-acute-care', 'discharge-planning-checklist', 'home-health-eligibility'],
    faqs: [
      { q: 'What is the "3-hour therapy rule" for IRFs?',
        a: 'To qualify for IRF admission under Medicare, patients generally must be able to tolerate and benefit from at least 3 hours of intensive therapy per day (physical, occupational, and/or speech), 5 days a week — or 15 hours across 7 consecutive days. Documentation must support the patient\u2019s need for and ability to participate in this intensity.' },
      { q: 'What is the therapy intensity in a SNF?',
        a: 'SNFs typically deliver 1\u20132 hours of therapy per day, tailored to what the patient can tolerate. SNFs are appropriate for patients who need skilled care and rehabilitation but cannot sustain IRF-level intensity.' },
      { q: 'How does Medicare cover a SNF stay?',
        a: 'Medicare Part A covers up to 100 days of SNF care after a qualifying 3-day inpatient hospital stay. Days 1\u201320 have no daily coinsurance; days 21\u2013100 have a daily coinsurance ($204/day in 2025, indexed annually).' },
      { q: 'Can a patient move from IRF to SNF?',
        a: 'Yes. It\u2019s common for a patient to complete a shorter IRF stay and step down to a SNF for continued sub-acute rehab, or vice versa if IRF was not the right initial fit.' },
    ],
    body: `Skilled nursing facilities (SNFs) and inpatient rehabilitation facilities (IRFs) are both post-acute options — and they are frequently confused, even inside hospitals. The difference matters clinically, financially, and for the patient\u2019s recovery trajectory.

This guide walks through the distinctions that determine the right placement.

## Who belongs in an IRF

Patients admitted to an IRF must:

1. **Require and be able to participate in intensive rehabilitation.** The regulatory threshold is generally 3 hours of therapy per day, 5 days a week, or 15 hours across 7 consecutive days.
2. **Need at least two therapy disciplines** (physical, occupational, or speech).
3. **Be medically stable** enough for daily therapy but still require close physician oversight — typically face-to-face visits at least 3 times per week.
4. **Have a reasonable expectation of significant functional improvement.**

Common IRF admissions: stroke, spinal cord injury, traumatic brain injury, major multiple trauma, hip fracture in patients likely to walk again, complex neurologic conditions.

## Who belongs in a SNF

SNFs serve a broader post-acute population. Patients admitted to a SNF typically:

1. **Need daily skilled nursing or skilled therapy services** (or both).
2. **Cannot tolerate 3 hours of therapy per day** but still benefit from 1\u20132 hours of daily therapy.
3. **Have complex care needs** — wound care, IV therapy, respiratory management, medication management — beyond what home health can provide.
4. **Need transitional support** before returning home.

Common SNF admissions: deconditioning after prolonged illness, wound care, uncomplicated joint replacement rehab, IV antibiotic completion, medically complex patients not needing LTACH.

## Payer coverage differences

**Medicare Part A — IRF:** IRF is paid under the IRF PPS (prospective payment system) using CMGs (case-mix groups). No 3-day rule applies to IRF.

**Medicare Part A — SNF:** SNF is covered under the SNF PPS using PDPM (Patient-Driven Payment Model). The 3-day prior hospital inpatient stay rule applies (waived under certain waivers).

**Medicare Advantage / commercial:** Both settings are typically subject to prior authorization; MA plans often have narrower networks and shorter approved lengths of stay.

## Length of stay

- **IRF median LOS:** ~13 days
- **SNF median LOS:** ~25\u201335 days (varies significantly by patient population)

## The decision framework

For each patient, ask:

- **Can they tolerate and benefit from 3 hours of daily therapy?** If yes and there's meaningful functional improvement expected, IRF.
- **Do they need daily skilled nursing beyond what home health can provide?** SNF.
- **Are they medically complex (vent, complex wounds, unstable)?** Consider LTACH, not SNF.
- **Do they have adequate home support and are willing to progress at home?** Consider home health with therapy.

## Documentation matters

For IRF admissions, the pre-admission screening (PAS), post-admission physician evaluation (PAPE), and interdisciplinary team conference must all be documented per Medicare requirements. A missing PAS is the single most common IRF documentation failure.

For SNF admissions, ensure the 3-day inpatient stay is documented (dates of admission and discharge from the hospital), and that skilled need is clearly justified in the transfer documentation.

## When you\u2019re not sure

If you\u2019re between SNF and IRF for a patient, the right move is a rehab consult — most hospitals have PM&R or a rehab liaison who can help. The wrong move is to send the referral to whoever has the fastest response time.`,
  },

  // ─────────────────────────────────────────────────────────────
  // 3. Levels of Post-Acute Care
  // ─────────────────────────────────────────────────────────────
  {
    slug: 'levels-of-post-acute-care',
    category: 'Levels of Care',
    title: 'The Levels of Post-Acute Care Explained',
    metaTitle: 'Levels of Post-Acute Care — SNF, IRF, LTACH, Home Health, Hospice | CaraLoom',
    metaDescription:
      'The full spectrum of post-acute care levels — SNF, home health, hospice, IRF, LTACH — with criteria, coverage, therapy intensity, and typical patient profiles.',
    keywords: [
      'levels of post acute care',
      'post acute care continuum',
      'SNF LTACH IRF home health',
      'post acute care options',
    ],
    excerpt:
      'A reference guide to the levels of post-acute care used by hospital case managers, with criteria, coverage, and typical patient profiles for each setting.',
    heroImage: NEUTRAL_HERO,
    heroAlt: 'Levels of post-acute care from SNF to hospice — a case-management reference',
    readMinutes: 8,
    datePublished: '2026-08-16',
    dateModified: '2026-08-16',
    reviewer: REVIEWER,
    tags: ['Levels of care', 'Post-acute', 'Case management'],
    related: ['snf-vs-irf', 'home-health-eligibility', 'hospice-eligibility'],
    faqs: [
      { q: 'What is the difference between LTACH and SNF?',
        a: 'LTACHs (long-term acute care hospitals) are licensed as hospitals and serve medically complex patients requiring extended stays — typically 25+ days on average. They handle ventilator weaning, complex wounds, and multi-system organ dysfunction. SNFs are skilled nursing settings, not hospitals, with less physician oversight and lower medical acuity thresholds.' },
      { q: 'Is assisted living considered post-acute care?',
        a: 'Not in the Medicare sense. Assisted living is a custodial residential setting — Medicare does not cover it. It\u2019s an appropriate long-term living option for patients who need help with ADLs but not daily skilled clinical care.' },
      { q: 'How do palliative care and hospice differ?',
        a: 'Palliative care is comfort-focused care that can accompany curative treatment at any stage of a serious illness. Hospice is specifically for patients with a terminal prognosis of \u22646 months if the illness runs its usual course, with a goal of comfort rather than cure. Both use interdisciplinary teams; hospice has a defined Medicare benefit.' },
    ],
    body: `Post-acute care is a spectrum, not a checklist. The right level for a given patient depends on medical acuity, functional status, expected trajectory, and payer coverage. This is a reference for the main options.

## Skilled Nursing Facility (SNF)

- **Who it serves:** Medically stable patients needing daily skilled nursing or skilled therapy after a hospital stay.
- **Physician oversight:** Not required daily. Physician visits are typically weekly initially, then per acuity.
- **Therapy intensity:** 1\u20132 hours per day.
- **Medicare coverage:** Part A after a 3-day inpatient hospital stay; up to 100 days with coinsurance from day 21.
- **Typical LOS:** 25\u201335 days.

See the detailed [SNF vs IRF](/guides/snf-vs-irf) guide for the placement criteria.

## Home Health Agency (HHA)

- **Who it serves:** Homebound patients with skilled intermittent nursing or therapy needs.
- **Physician oversight:** A face-to-face encounter (F2F) with the certifying physician within 90 days before or 30 days after start of care.
- **Therapy intensity:** Intermittent visits (typically 1\u20135 per week).
- **Medicare coverage:** Part A / Part B, no cost-share for covered services. Requires homebound status + skilled need.
- **Typical LOS:** 60-day episodes.

See [home health eligibility](/guides/home-health-eligibility) for detailed criteria.

## Inpatient Rehabilitation Facility (IRF)

- **Who it serves:** Patients who can tolerate 3 hours of therapy per day and are expected to make significant functional gains.
- **Physician oversight:** Face-to-face by a rehab physician at least 3 times per week.
- **Therapy intensity:** 3 hours per day, 5 days per week (or 15 hours across 7 consecutive days).
- **Medicare coverage:** Part A; no 3-day rule.
- **Typical LOS:** ~13 days.

Common IRF admissions: stroke, spinal cord injury, TBI, hip fracture in patients likely to walk, complex neurologic conditions.

## Long-Term Acute Care Hospital (LTACH)

- **Who it serves:** Medically complex patients requiring extended acute care — the population that is too complex or unstable for SNF but no longer needs a traditional acute-care hospital bed.
- **Physician oversight:** Daily. LTACHs are licensed as hospitals.
- **Therapy intensity:** Variable; adjunctive to the medical care.
- **Medicare coverage:** Part A. Payment based on the LTACH PPS with a "site-neutral" rate that limits payment for patients not meeting LTACH criteria.
- **Typical LOS:** 25+ days.

Common LTACH admissions: prolonged mechanical ventilation, complex wound care, multi-organ failure recovery.

## Hospice

- **Who it serves:** Patients with a terminal prognosis of \u22646 months if the illness runs its usual course, who elect comfort-focused rather than curative care.
- **Physician oversight:** Attending physician + hospice medical director certify the terminal prognosis at initial and each subsequent benefit period.
- **Care team:** Interdisciplinary — physician, RN, aide, social worker, chaplain, bereavement.
- **Medicare coverage:** Hospice benefit under Part A. No cost-share for covered services. Patient waives curative treatment for the terminal illness.
- **Setting:** Most commonly at home; also in nursing facilities and inpatient hospice houses.

See [hospice eligibility](/guides/hospice-eligibility) for detailed criteria.

## Palliative Care

- **Who it serves:** Anyone with a serious illness — at any stage, alongside curative treatment.
- **Physician oversight:** Palliative care team consult, typically outpatient or inpatient.
- **Medicare coverage:** Fee-for-service (no dedicated benefit); many services covered under Part B.
- **Setting:** Hospital, clinic, or home-based programs.

## Assisted Living / Memory Care

- **Who it serves:** Individuals needing assistance with ADLs but not daily skilled clinical care. Memory care is a specialized assisted-living unit for patients with dementia.
- **Medicare coverage:** None. Paid out of pocket, long-term care insurance, Medicaid HCBS waivers (state-dependent), or veteran benefits.
- **Setting:** Residential facility with 24/7 staff.

## Durable Medical Equipment (DME)

- **What it is:** Equipment prescribed to support a patient at home — oxygen, hospital beds, wheelchairs, CPAPs, ostomy supplies, insulin pumps.
- **Medicare coverage:** Part B (80% covered, 20% coinsurance after deductible).
- **Coordination:** Best coordinated in parallel with home health referral so equipment arrives at home before or on the day of discharge.

## Choosing the right level

The two questions to anchor every decision:

1. **What clinical care does the patient need in the next 30 days?**
2. **What is the least-intensive setting that can safely deliver that care?**

Over-placing a patient (SNF when home health would work) wastes coverage days and delays independence. Under-placing (home health when SNF is appropriate) drives readmissions.

Discharge planning software should surface all qualifying levels for a given patient, filtered by payer and clinical fit, rather than defaulting to the most familiar option.`,
  },

  // ─────────────────────────────────────────────────────────────
  // 4. Home Health Eligibility
  // ─────────────────────────────────────────────────────────────
  {
    slug: 'home-health-eligibility',
    category: 'Eligibility',
    title: 'Medicare Home Health Eligibility — The Full Requirements',
    metaTitle: 'Medicare Home Health Eligibility — Homebound, Skilled Need, F2F | CaraLoom',
    metaDescription:
      'Medicare home health eligibility explained for case managers — homebound status, skilled need, face-to-face encounter, physician certification, and common documentation pitfalls.',
    keywords: [
      'medicare home health eligibility',
      'home health eligibility criteria',
      'homebound requirements',
      'face to face encounter home health',
      'home health CoP',
    ],
    excerpt:
      'The complete Medicare home health eligibility framework — with the four criteria, F2F documentation requirements, and the most common denials.',
    heroImage: NEUTRAL_HERO,
    heroAlt: 'Medicare home health eligibility criteria reference',
    readMinutes: 7,
    datePublished: '2026-08-16',
    dateModified: '2026-08-16',
    reviewer: REVIEWER,
    tags: ['Home health', 'Medicare', 'Eligibility'],
    related: ['discharge-planning-checklist', 'levels-of-post-acute-care', 'hospice-eligibility'],
    faqs: [
      { q: 'What counts as "homebound"?',
        a: 'A patient is considered homebound if leaving the home requires considerable and taxing effort, and the patient meets one of two criteria: (1) needs the aid of supportive devices, assistance from another person, or special transportation, OR has a condition that makes leaving home medically contraindicated; AND (2) there exists a normal inability to leave home, and leaving home requires considerable and taxing effort. Absences from the home must be infrequent, of short duration, or attributable to medical treatment.' },
      { q: 'What is the F2F encounter requirement?',
        a: 'A face-to-face encounter with the certifying physician or an allowed non-physician practitioner must occur within 90 days before or 30 days after the home health start of care. The encounter must be related to the primary reason for home health care and must be documented in the medical record. Missing or inadequate F2F documentation is the top cause of home health denials.' },
      { q: 'Does the patient need a hospital stay first?',
        a: 'No. Unlike SNF, home health does not require a prior hospital stay. Home health can be initiated from any setting — inpatient, ED, physician office, or community.' },
    ],
    body: `Medicare home health is a valuable, cost-effective post-acute benefit — but its eligibility rules are specific and every denial is painful. This guide walks through the framework.

## The four eligibility criteria

A Medicare beneficiary qualifies for home health services under Part A or Part B when **all** of the following are met:

1. **Under the care of a physician**, and receiving services under a plan of care established and periodically reviewed by that physician.
2. **In need of skilled services** — intermittent skilled nursing, physical therapy, speech-language pathology, or continued occupational therapy.
3. **Homebound.** (See the definition below.)
4. **A face-to-face encounter** with the certifying physician (or allowed NPP) has occurred within the required window.

## The "homebound" definition

A patient is homebound if **both** conditions are met:

**Condition 1** (either 1a or 1b):
- **1a:** The patient needs supportive devices (walker, cane, wheelchair), assistance from another person, or special transportation to leave the home due to illness or injury.
- **1b:** The patient has a condition where leaving the home is medically contraindicated.

**Condition 2:**
- There exists a normal inability to leave home, AND
- Leaving home requires a considerable and taxing effort.

Absences from home must be infrequent, of short duration, or attributable to receiving medical treatment (dialysis, chemotherapy, doctor visits, adult day care for medical purposes). Attendance at religious services is allowed without affecting homebound status.

## Skilled need — what qualifies

**Skilled nursing:** wound care, IV therapy, injections requiring nursing skill, catheter/ostomy care, medication management for complex regimens, disease monitoring and teaching, tube feedings.

**Skilled therapy:** physical, occupational, or speech therapy that is reasonable and necessary based on the patient\u2019s condition, that requires the skills of a qualified therapist, and that is expected to result in significant improvement (or, for maintenance therapy, requires the skill of a therapist to maintain the patient\u2019s current condition or prevent decline).

Personal care (bathing, dressing, meal preparation) alone is not a skilled need under Medicare — but a home health aide can provide personal care alongside a qualifying skilled service.

## The F2F encounter

**Timing:** within 90 days before OR 30 days after the start of home health care.

**Who can perform it:** the certifying physician, an NPP working with the certifying physician (NP, PA, CNS), or the physician who attended the patient in the hospital or in the acute or post-acute setting from which the patient was discharged.

**What must be documented:**
- Date of the encounter.
- Encounter was related to the primary reason for home health care.
- Clinical justification of the need for home health services (homebound status and skilled need must be supported by the physician\u2019s documentation).

**The top denial reason:** F2F documentation exists but the clinical narrative doesn\u2019t clearly justify homebound status and skilled need. A checkbox is not sufficient. Discharge summaries can serve as F2F documentation if the treating physician\u2019s note supports the requirements.

## Common eligibility misconceptions

- **"They need to be bedbound."** No. Homebound does not mean bedbound.
- **"They can\u2019t go to any doctor appointments."** Attending medical appointments is allowed and doesn\u2019t break homebound status.
- **"They can\u2019t drive or leave the house at all."** Occasional short absences are permitted.
- **"They must have had a hospital stay."** Home health does not require a prior inpatient stay.
- **"Once they\u2019re admitted, they\u2019re covered indefinitely."** No — home health is delivered in 60-day episodes with recertification required.

## Practical tips for case managers

- **Get the F2F documentation right before the referral goes out.** A referral packet without adequate F2F documentation costs the home health agency time and can delay start of care.
- **Document homebound status in the discharge summary.** Use specific, clinical language, not template checkboxes.
- **Include a clear list of the skilled needs.** Wound care order, medication reconciliation issues, therapy goals — specifics matter.
- **Confirm the patient is willing.** Home health is elective; the patient must consent.

If you\u2019re coordinating home health referrals from a hospital and want the F2F, plan of care, and NOA to flow in one signed thread, that\u2019s what [CaraLoom\u2019s discharge planning platform](/discharge-planning-software) is built to do.`,
  },

  // ─────────────────────────────────────────────────────────────
  // 5. Hospice Eligibility
  // ─────────────────────────────────────────────────────────────
  {
    slug: 'hospice-eligibility',
    category: 'Eligibility',
    title: 'Medicare Hospice Eligibility — The Six-Month Prognosis & More',
    metaTitle: 'Medicare Hospice Eligibility — Prognosis, Election, LCDs | CaraLoom',
    metaDescription:
      'Medicare hospice eligibility explained — six-month terminal prognosis, election statement, physician certifications, benefit periods, and LCDs by diagnosis.',
    keywords: [
      'medicare hospice eligibility',
      'hospice election requirements',
      'six month prognosis hospice',
      'hospice LCD',
      'hospice benefit periods',
    ],
    excerpt:
      'Medicare hospice eligibility explained clearly — the six-month prognosis rule, election requirements, benefit periods, and how LCDs guide diagnosis-specific criteria.',
    heroImage: NEUTRAL_HERO,
    heroAlt: 'Medicare hospice eligibility criteria reference',
    readMinutes: 6,
    datePublished: '2026-08-16',
    dateModified: '2026-08-16',
    reviewer: REVIEWER,
    tags: ['Hospice', 'Medicare', 'End of life'],
    related: ['home-health-eligibility', 'levels-of-post-acute-care', 'discharge-planning-checklist'],
    faqs: [
      { q: 'Does hospice mean "giving up"?',
        a: 'No. Hospice is a specialized model of care that focuses on comfort and quality of life for patients with a terminal prognosis. Patients continue to receive care — the goal shifts from cure to comfort. Many patients live longer on hospice than they were expected to, in part because of the comprehensive symptom management and family support.' },
      { q: 'Can a patient leave hospice if their condition improves?',
        a: 'Yes. A patient can revoke the hospice election at any time and return to standard Medicare coverage. If the terminal prognosis no longer applies (the patient stabilizes or improves), the hospice will discharge the patient. The patient can also re-elect hospice later if their condition progresses.' },
      { q: 'What is the hospice benefit period structure?',
        a: 'The Medicare hospice benefit is divided into two 90-day periods followed by an unlimited number of 60-day periods. Each period requires physician recertification of the six-month terminal prognosis. Beginning with the third benefit period, a face-to-face encounter by a hospice physician or NP is required.' },
    ],
    body: `Medicare hospice is an underused benefit, largely because clinicians and families delay referral. Understanding the eligibility criteria clearly is the first step to making better and more timely referrals.

## The core eligibility rule

A Medicare beneficiary is eligible for the hospice benefit if:

1. They are entitled to Medicare Part A.
2. Their attending physician and the hospice medical director certify that they have a **terminal illness with a life expectancy of six months or less if the illness runs its usual course**.
3. They elect the hospice benefit and waive Medicare coverage for treatment of the terminal illness (curative treatment for other conditions is still covered).
4. They receive care from a Medicare-approved hospice program.

## What "six months or less" actually means

The six-month prognosis is a clinical judgment, not a guarantee. Patients frequently live longer than the initial certification — and are re-certified as long as the terminal prognosis continues to apply.

Under-referral is the norm, not the exception. National data suggest the median length of hospice stay is about 18 days, well below what the six-month benefit supports. Referring earlier, when appropriate, generally improves symptom management and family experience.

## LCDs — diagnosis-specific criteria

CMS Local Coverage Determinations (LCDs) provide diagnosis-specific criteria that help guide when a patient meets the six-month prognosis. Common LCDs:

- **Cancer** — metastatic disease with declining functional status (PPS \u226450%), typically.
- **Dementia (Alzheimer\u2019s and related)** — FAST scale 7 or beyond, with a comorbid condition within the last 12 months (aspiration pneumonia, pyelonephritis, sepsis, decubitus ulcer stage 3-4, or 10% weight loss).
- **Heart disease** — NYHA Class IV symptoms optimally treated, EF \u226420%, and additional supporting criteria.
- **Pulmonary disease** — dyspnea at rest, FEV1 <30% predicted, cor pulmonale, hypoxemia, or hypercapnia.
- **Renal disease** — not seeking dialysis or transplant, with creatinine clearance <10 mL/min and creatinine >8 mg/dL (or lower for diabetics).
- **Stroke and coma** — dependence in all ADLs, PPS \u226440%, and inability to swallow.
- **HIV** — CD4 <25 or persistent viral load >100,000, with a specific opportunistic infection or comorbidity.

LCDs are guidelines, not rigid rules. Clinical judgment about the overall trajectory still applies.

## The election statement

To elect hospice, the patient (or their authorized representative) signs an election statement that includes:

- The specific hospice provider that will provide care.
- The patient\u2019s acknowledgment that they understand the nature of hospice care.
- The patient\u2019s acknowledgment that they waive Medicare payments for services to treat the terminal illness (with the exception of the attending physician\u2019s services).
- The effective date of election.

The patient may name an attending physician who is not employed by the hospice; that physician\u2019s services continue to be billable to Medicare.

## Benefit periods and re-certification

The Medicare hospice benefit is divided into:

- **Two initial 90-day periods**, followed by
- **An unlimited number of 60-day periods.**

Each period requires physician recertification of the terminal prognosis. Beginning with the third benefit period (day 180+), a face-to-face encounter by a hospice physician or hospice NP is required, and the encounter must occur no more than 30 days before the start of the third or later benefit period.

## What hospice covers

Under the hospice benefit, the hospice team provides all care related to the terminal illness, including:

- Physician services (attending physician remains separately billable if not a hospice employee).
- Skilled nursing care.
- Medical social services.
- Counseling (spiritual, dietary, bereavement).
- Home health aide and homemaker services.
- Physical therapy, occupational therapy, and speech-language pathology.
- Medical equipment and supplies related to the terminal illness.
- Medications for symptom control related to the terminal illness.
- Short-term inpatient care for pain and symptom management.
- Respite care (up to 5 consecutive days at a time, for caregiver relief).

Curative treatment for the terminal illness is not covered under the hospice benefit while it is elected.

## The four levels of hospice care

1. **Routine home care** — the most common level; care in the patient\u2019s home.
2. **Continuous home care** — around-the-clock nursing during a period of crisis.
3. **General inpatient care (GIP)** — inpatient hospice for symptoms that cannot be managed at home.
4. **Respite care** — short-term inpatient care to give the primary caregiver relief.

## Practical tips for referring

- **Don\u2019t wait for the family to ask.** Physicians and case managers can and should initiate the hospice conversation when the six-month prognosis is met.
- **Involve the palliative care team early.** In many hospitals, palliative care and hospice are complementary. Palliative involvement can start well before hospice election.
- **Coordinate the transition carefully.** The move to hospice is often abrupt for families; a warm hand-off between hospital care team and hospice team is invaluable.
- **Get the certification documentation right.** LCD criteria should be referenced in the certification narrative, not just in a checkbox.

For hospitals coordinating hospice referrals as part of discharge planning, [CaraLoom\u2019s post-acute care platform](/post-acute-care-platform) surfaces qualifying hospice providers alongside SNF, home health, IRF, and LTACH options in one search.`,
  },
];

export function getGuide(slug) {
  return GUIDES.find((g) => g.slug === slug);
}
